/**
 * Chart page — AI-powered chart suggestion and creation.
 *
 * Flow:
 *   1. Read the Excel selection.
 *   2. AI analyzes the data and suggests chart types.
 *   3. User picks a suggestion → ActionPreviewPanel confirms before creation.
 *   4. Chart is created in Excel after confirmation.
 */

import React, { useState, useCallback } from 'react';
import {
  Button,
  Text,
  Badge,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  DocumentSearchRegular,
  DataBarVerticalRegular,
  ArrowResetRegular,
  AddRegular,
} from '@fluentui/react-icons';
import type { AnalysisResult, SuggestedChart } from '@excel-ai-studio/shared';
import { analyzeDataset } from '../services/api.client';
import { readSelectionInfo, createChart, type SelectionInfo } from '../office';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorDisplay } from '../components/common/ErrorDisplay';
import { ResultCard } from '../components/common/ResultCard';
import { ActionPreviewPanel } from '../components/common/ActionPreviewPanel';
import { ConfidenceBadge } from '../components/common/ConfidenceBadge';
import { getSettings } from '../store/settings.store';
import { addHistoryEntry } from '../store/history.store';

// Map AI chart type → Excel ChartType enum string
// Only types guaranteed to work across Excel desktop + web
const CHART_TYPE_MAP: Record<string, string> = {
  bar: 'ColumnClustered',
  line: 'Line',
  pie: 'Pie',
  scatter: 'XYScatter',
  histogram: 'ColumnClustered', // Histogram requires ExcelApi 1.9+ — fallback to columns
  heatmap: 'ColumnClustered',   // No native heatmap — fallback to columns
};

const CHART_TYPE_LABEL: Record<string, string> = {
  bar: 'Barres',
  line: 'Lignes',
  pie: 'Secteurs',
  scatter: 'Nuage de points',
  histogram: 'Histogramme',
  heatmap: 'Carte de chaleur',
};

export const ChartPage: React.FC = () => {
  const [selectionInfo, setSelectionInfo] = useState<SelectionInfo>();
  const [loading, setLoading] = useState(false);
  const [charts, setCharts] = useState<SuggestedChart[]>([]);
  const [summary, setSummary] = useState<string>();
  const [error, setError] = useState<string>();

  // Preview state: which chart index is pending confirmation
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);

  const resetAll = () => {
    setSelectionInfo(undefined);
    setCharts([]);
    setSummary(undefined);
    setError(undefined);
    setPreviewIndex(null);
  };

  const handleReadSelection = async () => {
    setError(undefined);
    setCharts([]);
    setSummary(undefined);
    setPreviewIndex(null);
    setLoading(true);

    try {
      const info = await readSelectionInfo();
      if (!info.values || info.values.length === 0) {
        setError('Aucune cellule selectionnee. Selectionnez des donnees dans Excel.');
        setSelectionInfo(undefined);
        return;
      }
      setSelectionInfo(info);
    } catch (err) {
      setError((err as Error).message);
      setSelectionInfo(undefined);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!selectionInfo) return;
    const startedAt = Date.now();
    const settings = getSettings();
    setError(undefined);
    setCharts([]);
    setSummary(undefined);
    setPreviewIndex(null);
    setLoading(true);

    try {
      const result: AnalysisResult = await analyzeDataset({ values: selectionInfo.values, language: 'fr' });
      setCharts([...result.suggestedCharts]);
      setSummary(result.summary);
      addHistoryEntry(
        'analysis',
        settings.provider,
        settings.model,
        `Suggestion graphiques (${selectionInfo.rowCount}x${selectionInfo.colCount})`,
        true,
        undefined,
        {
          module: 'chart',
          durationMs: Date.now() - startedAt,
          rangeAddress: selectionInfo.address,
        },
      );
    } catch (err) {
      setError((err as Error).message);
      addHistoryEntry(
        'analysis',
        settings.provider,
        settings.model,
        `Suggestion graphiques (${selectionInfo.rowCount}x${selectionInfo.colCount})`,
        false,
        (err as Error).message,
        {
          module: 'chart',
          durationMs: Date.now() - startedAt,
          rangeAddress: selectionInfo.address,
        },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChart = useCallback(async () => {
    if (previewIndex === null || !selectionInfo) return;
    const chart = charts[previewIndex];
    if (!chart) return;

    const excelType = CHART_TYPE_MAP[chart.type] ?? 'ColumnClustered';
    await createChart(excelType, chart.title, selectionInfo.address);
    setTimeout(() => setPreviewIndex(null), 1500);
  }, [previewIndex, charts, selectionInfo]);

  return (
    <div>
      <div className="card">
        <Text className="section-title">Graphique IA</Text>
        <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: 8 }}>
          Selectionnez des donnees dans Excel. L&apos;IA analysera votre dataset et
          suggerera les meilleurs types de graphiques.
        </Text>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            size="small"
            appearance="primary"
            icon={<DocumentSearchRegular />}
            onClick={handleReadSelection}
            disabled={loading}
          >
            Lire la selection
          </Button>
          {selectionInfo && !loading && (
            <Button
              size="small"
              appearance="primary"
              icon={<DataBarVerticalRegular />}
              onClick={handleAnalyze}
            >
              Suggerer des graphiques
            </Button>
          )}
          {(selectionInfo || charts.length > 0) && (
            <Button size="small" appearance="subtle" icon={<ArrowResetRegular />} onClick={resetAll}>
              Reinitialiser
            </Button>
          )}
        </div>

        {selectionInfo && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Badge appearance="outline" size="small" color="informative">
              {selectionInfo.address}
            </Badge>
            <Text size={200} style={{ color: 'var(--text-secondary)' }}>
              {selectionInfo.rowCount} lignes x {selectionInfo.colCount} colonnes
            </Text>
          </div>
        )}
      </div>

      {loading && <LoadingSpinner label="Analyse en cours..." />}
      {error && <ErrorDisplay message={error} onDismiss={() => setError(undefined)} />}

      {summary && (
        <ResultCard
          title="Resume"
          icon={<DataBarVerticalRegular />}
          variant="analysis"
        >
          <Text size={200} style={{ whiteSpace: 'pre-wrap' }}>{summary}</Text>
          <ConfidenceBadge
            meta={{
              score:
                (charts.length >= 3 ? 84 : charts.length >= 1 ? 72 : 50) -
                (selectionInfo && selectionInfo.colCount < 2 ? 12 : 0),
              reasons: [
                charts.length > 0
                  ? `${charts.length} suggestion(s) de graphique generee(s).`
                  : 'Aucune suggestion automatique: jeu de donnees possiblement limite.',
                selectionInfo && selectionInfo.colCount < 2
                  ? 'Une seule colonne detectee: options de visualisation reduites.'
                  : 'Structure des colonnes exploitable pour la visualisation.',
              ],
            }}
          />
        </ResultCard>
      )}

      {charts.length > 0 && (
        <ResultCard
          title={`Graphiques suggeres (${charts.length})`}
          icon={<DataBarVerticalRegular />}
          variant="suggestion"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {charts.map((chart, i) => (
              <div key={i} style={{ padding: '10px 14px', background: 'var(--colorNeutralBackground2)', borderRadius: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <Badge size="small" appearance="tint" color="brand">
                    {CHART_TYPE_LABEL[chart.type] ?? chart.type}
                  </Badge>
                  <Text size={200} weight="semibold">{chart.title}</Text>
                  <Button
                    size="small"
                    appearance={previewIndex === i ? 'subtle' : 'primary'}
                    icon={<AddRegular />}
                    disabled={previewIndex !== null && previewIndex !== i}
                    onClick={() => setPreviewIndex(previewIndex === i ? null : i)}
                    style={{ marginLeft: 'auto' }}
                  >
                    Creer
                  </Button>
                </div>
                <Text size={200} style={{ color: 'var(--text-secondary)', display: 'block' }}>
                  {chart.description}
                </Text>
                <Text size={200} style={{ color: 'var(--text-secondary)', fontStyle: 'italic', display: 'block', marginTop: 4 }}>
                  Colonnes : {chart.columns.join(', ')}
                </Text>

                {/* Preview confirmation for this chart */}
                {previewIndex === i && selectionInfo && (
                  <ActionPreviewPanel
                    impact={{
                      target: selectionInfo.address,
                      description: `Un graphique "${CHART_TYPE_LABEL[chart.type] ?? chart.type}" sera cree a partir de la selection. Le graphique sera place a cote des donnees.`,
                    }}
                    onConfirm={handleCreateChart}
                    onCancel={() => setPreviewIndex(null)}
                    confirmLabel="Creer le graphique"
                    confirmIcon={<AddRegular />}
                  />
                )}
              </div>
            ))}
          </div>
        </ResultCard>
      )}

      {charts.length === 0 && summary && (
        <MessageBar intent="info" style={{ borderRadius: 4 }}>
          <MessageBarBody>Aucun graphique suggere pour ce dataset.</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
};
